import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, type Me, type ViewPerson } from '../api.js';
import { INVITED_VIEW, VIEW_MARKER, useViewing, viewBandText, type Viewing } from '../view-as.js';
import type { Access } from '../parts/access.js';

const People = createContext<ViewPerson[] | null>(null);
/** C3 grants the chooser; an active keeper view retains switching and End. */
export function mayBeKeeper(me: Me, access: Access | null): boolean {
  return Boolean(me.viewing || access?.viewing) || access?.person?.keeper === true;
}
export function KeeperViewProvider({ asks = true, children }: { asks?: boolean; children: ReactNode }) {
  const [people, setPeople] = useState<ViewPerson[] | null>(null);
  useEffect(() => {
    if (!asks) return;
    let cancelled = false;
    void api.viewAsPeople().then((r) => { if (!cancelled) setPeople(r.people); }).catch(() => {});
    return () => { cancelled = true; };
  }, [asks]);
  return <People.Provider value={asks ? people : null}>{children}</People.Provider>;
}
export function useKeeperChooser(): boolean { return useContext(People) !== null; }

export function KeeperViewChooser() {
  const people = useContext(People);
  const view = useViewing();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (people === null) return null;
  const choose = async (principalId: string) => {
    setBusy(true); setError(null);
    try {
      if (view) { await api.endViewAs('end'); sessionStorage.removeItem(VIEW_MARKER); }
      const { sessionId } = await api.startViewAs(principalId);
      sessionStorage.setItem(VIEW_MARKER, sessionId);
      window.location.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e)); setBusy(false);
      if (view) window.location.reload();
    }
  };
  const filtered = people.filter((p) => `${p.displayName} ${p.group ?? ''} ${p.upn}`.toLowerCase().includes(q.toLowerCase()));
  return <div className="keeper-chooser">
    <button type="button" aria-expanded={open} aria-label="View as" onClick={() => setOpen(!open)}>View as ▾</button>
    {open ? <div className="keeper-people">
      <input aria-label="Filter people to view" value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
      <ul>{filtered.map((p) => <li key={p.principalId}><button type="button" disabled={busy} onClick={() => void choose(p.principalId)}>
        <strong>{p.displayName}</strong><span>{p.group ?? ''} · {p.status === 'invited' ? 'not signed in yet' : p.status}</span>
      </button></li>)}</ul>
    </div> : null}
    {error ? <span role="alert">{error}</span> : null}
  </div>;
}

export function KeeperViewBand({ view }: { view: Viewing }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const end = async () => {
    setBusy(true);
    try { await api.endViewAs('end'); sessionStorage.removeItem(VIEW_MARKER); window.location.reload(); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); setBusy(false); }
  };
  return <div className="keeper-band" role="status">
    <div><p>{viewBandText(view)}</p>{view.status === 'invited' ? <p>{INVITED_VIEW}</p> : null}</div>
    <KeeperViewChooser /><button type="button" disabled={busy} onClick={() => void end()}>End</button>
    {error ? <span role="alert">{error}</span> : null}
  </div>;
}
